import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query';
import type {
  ClientAutoMatchReport,
  Contract,
  ContractList,
  IgnoredClientList,
  MapClientResponse,
  UnmappedClientsResponse,
} from '@/types/api';

/**
 * Hooks für Vertrags-Sync und Client-Mapping.
 *
 * - `useContracts({ customerId })` - liefert Verträge eines Kunden (typisch
 *   1, kann mehr sein wenn Kunde mehrere Recurring-Invoices hat).
 * - `useUnmappedContracts()` - Wizard-Quelle: offene Verträge GRUPPIERT
 *   nach Invoice-Ninja-Client inkl. Top-3-Vorschlägen pro Client.
 * - `useMapClient()` - mappt ALLE Verträge eines IN-Clients auf einen Kunden.
 * - `useIgnoreClient()` / `useUnignoreClient()` / `useIgnoredClients()` -
 *   Ignore-Flow für Hosting-only-Clients ohne Wartungsvertrag.
 * - `useAutoMatch()`, `useMapContract()`, `useUnmapContract()` - Mutations
 *   mit gezielter Cache-Invalidation, damit der Wizard ohne Reload aktuell
 *   bleibt.
 */

export interface ContractsFilter {
  customerId?: string;
  mapped?: boolean;
  status?: 'active' | 'paused' | 'expired' | 'deleted';
  cursor?: string;
  limit?: number;
}

export function useContracts(
  filter: ContractsFilter = {}
): ReturnType<typeof useQuery<ContractList>> {
  const params: Record<string, string | number | boolean | undefined> = {
    'filter[customer_id]': filter.customerId,
    'filter[mapped]': filter.mapped,
    'filter[status]': filter.status,
    cursor: filter.cursor,
    limit: filter.limit ?? 50,
  };
  return useQuery<ContractList>({
    queryKey: queryKeys.contracts.list(filter as Record<string, unknown>),
    queryFn: () => api.get<ContractList>('/contracts', params),
  });
}

/**
 * Liefert die Verträge EINES Kunden (i.d.R. einer, mehrere möglich).
 * Convenience über `useContracts` damit Customer-Detail-Tab kompakt bleibt.
 */
export function useCustomerContracts(
  customerId: string | undefined
): ReturnType<typeof useQuery<ContractList>> {
  return useQuery<ContractList>({
    queryKey: queryKeys.contracts.byCustomer(customerId ?? ''),
    queryFn: () =>
      api.get<ContractList>('/contracts', {
        'filter[customer_id]': customerId,
        limit: 20,
      }),
    enabled: Boolean(customerId),
  });
}

/** Client-gruppierte Sicht auf alle ungemappten Verträge (ohne ignorierte Clients). */
export function useUnmappedContracts(): ReturnType<typeof useQuery<UnmappedClientsResponse>> {
  return useQuery<UnmappedClientsResponse>({
    queryKey: queryKeys.contracts.unmapped,
    queryFn: () => api.get<UnmappedClientsResponse>('/contracts/unmapped'),
  });
}

export interface AutoMatchInput {
  threshold?: number;
  /** Wenn true, werden Mappings >= threshold direkt gesetzt. */
  commit?: boolean;
}

export function useAutoMatch(): ReturnType<
  typeof useMutation<ClientAutoMatchReport, Error, AutoMatchInput>
> {
  const qc = useQueryClient();
  return useMutation<ClientAutoMatchReport, Error, AutoMatchInput>({
    mutationFn: (input) =>
      api.post<ClientAutoMatchReport, AutoMatchInput>('/contracts/auto-match', input),
    onSuccess: (data) => {
      if (data.applied_count > 0) {
        // Cache invalidieren: Listen + Customer-Stats betroffen.
        void qc.invalidateQueries({ queryKey: queryKeys.contracts.all });
        void qc.invalidateQueries({ queryKey: queryKeys.customers.all });
      }
    },
  });
}

export interface MapClientInput {
  invoiceNinjaClientId: string;
  customerId: string;
}

/** Mappt alle offenen Verträge eines IN-Clients in einem Schritt auf einen Kunden. */
export function useMapClient(): ReturnType<
  typeof useMutation<MapClientResponse, Error, MapClientInput>
> {
  const qc = useQueryClient();
  return useMutation<MapClientResponse, Error, MapClientInput>({
    mutationFn: ({ invoiceNinjaClientId, customerId }) =>
      api.post<MapClientResponse, { invoice_ninja_client_id: string; customer_id: string }>(
        '/contracts/map-client',
        { invoice_ninja_client_id: invoiceNinjaClientId, customer_id: customerId }
      ),
    onSuccess: (_res, { customerId }) => {
      void qc.invalidateQueries({ queryKey: queryKeys.contracts.all });
      void qc.invalidateQueries({ queryKey: queryKeys.customers.detail(customerId) });
      void qc.invalidateQueries({ queryKey: queryKeys.customers.all });
    },
  });
}

export interface IgnoreClientInput {
  invoiceNinjaClientId: string;
  /** Optionale Notiz, warum der Client kein Wartungskunde ist. */
  note?: string | null;
}

/** Markiert einen IN-Client als "kein Wartungskunde" - er verschwindet aus der Liste. */
export function useIgnoreClient(): ReturnType<
  typeof useMutation<void, Error, IgnoreClientInput>
> {
  const qc = useQueryClient();
  return useMutation<void, Error, IgnoreClientInput>({
    mutationFn: ({ invoiceNinjaClientId, note }) =>
      api.post<void, { invoice_ninja_client_id: string; note: string | null }>(
        '/contracts/ignore-client',
        { invoice_ninja_client_id: invoiceNinjaClientId, note: note ?? null }
      ),
    onSuccess: () => {
      // unmapped-Liste UND ignored-Liste hängen unter contracts.*
      void qc.invalidateQueries({ queryKey: queryKeys.contracts.all });
    },
  });
}

/** Hebt eine Ignore-Markierung wieder auf (Client erscheint erneut im Wizard). */
export function useUnignoreClient(): ReturnType<typeof useMutation<void, Error, string>> {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (invoiceNinjaClientId) =>
      api.delete<void>(`/contracts/ignore-client/${encodeURIComponent(invoiceNinjaClientId)}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.contracts.all });
    },
  });
}

/** Liste aller ignorierten Clients (lazy - nur laden wenn Bereich offen). */
export function useIgnoredClients(
  opts: { enabled?: boolean } = {}
): ReturnType<typeof useQuery<IgnoredClientList>> {
  return useQuery<IgnoredClientList>({
    queryKey: queryKeys.contracts.ignoredClients,
    queryFn: () => api.get<IgnoredClientList>('/contracts/ignored-clients'),
    enabled: opts.enabled ?? true,
  });
}

export interface MapContractInput {
  contractId: string;
  customerId: string;
  confirmOverwrite?: boolean;
}

export function useMapContract(): ReturnType<
  typeof useMutation<Contract, Error, MapContractInput>
> {
  const qc = useQueryClient();
  return useMutation<Contract, Error, MapContractInput>({
    mutationFn: ({ contractId, customerId, confirmOverwrite }) =>
      api.post<Contract, { confirm_overwrite?: boolean }>(
        `/contracts/${contractId}/map-to-customer/${customerId}`,
        confirmOverwrite ? { confirm_overwrite: true } : {}
      ),
    onSuccess: (contract) => {
      void qc.invalidateQueries({ queryKey: queryKeys.contracts.all });
      if (contract.customer_id) {
        void qc.invalidateQueries({
          queryKey: queryKeys.contracts.byCustomer(contract.customer_id),
        });
        void qc.invalidateQueries({ queryKey: queryKeys.customers.detail(contract.customer_id) });
      }
    },
  });
}

export function useUnmapContract(): ReturnType<typeof useMutation<Contract, Error, string>> {
  const qc = useQueryClient();
  return useMutation<Contract, Error, string>({
    mutationFn: (contractId) => api.post<Contract>(`/contracts/${contractId}/unmap`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.contracts.all });
    },
  });
}
